import React from 'react'
import { CUSTOMER_LOGOS } from '@/utils/constants'

// Default logo size map for consistent sizing across all pages
const DEFAULT_LOGO_SIZE_MAP: Record<string, string> = {
  "ldoc": "h-16 w-36",    // Louisiana Department of Corrections
  "ahs": "h-16 w-36",     // Alberta Health Services
  "telus": "h-16 w-36",   // Telus
  "nhs": "h-16 w-36",     // NHS
}

interface CustomerLogosProps {
  /** Array of logo indices to display from CUSTOMER_LOGOS array (deprecated - use logoUids instead) */
  logoIndices?: number[]
  /** Array of logo UIDs to display from CUSTOMER_LOGOS array */
  logoUids?: string[]
  /** Number of logos to display (defaults to first 3) */
  count?: number
  /** Custom className for the container */
  className?: string
  /** Display mode: 'card' (default) or 'simple' */
  mode?: 'card' | 'simple'
  /** Custom className for individual logo images */
  logoClassName?: string
  /** Map of logo UIDs to custom size classes for the logo image inside cards */
  logoSizeMap?: Record<string, string>
}

const CustomerLogos: React.FC<CustomerLogosProps> = ({ 
  logoIndices, 
  logoUids,
  count = 3, 
  className = "mt-6 grid grid-cols-1 sm:grid-cols-3 justify-items-center gap-4 sm:gap-6",
  mode = 'card',
  logoClassName = "",
  logoSizeMap = DEFAULT_LOGO_SIZE_MAP
}) => {
  // Determine which logos to display
  let logosToShow
  
  if (logoUids) {
    // Use UIDs (preferred method)
    logosToShow = logoUids
      .map(uid => CUSTOMER_LOGOS.find(logo => logo.uid === uid))
      .filter((logo): logo is NonNullable<typeof logo> => logo !== undefined)
  } else if (logoIndices) {
    // Fallback to indices (deprecated)
    logosToShow = logoIndices.map(index => CUSTOMER_LOGOS[index]).filter((logo): logo is NonNullable<typeof logo> => logo !== undefined)
  } else {
    // Default: show first N logos
    logosToShow = CUSTOMER_LOGOS.slice(0, count)
  }

  return (
    <div className={className} id="logos">
      {logosToShow.map((logo) => {
        const customLogoSize = logoSizeMap[logo.uid] || "h-10 w-32"
        
        return mode === 'simple' ? (
          <img 
            key={logo.src}
            src={logo.src} 
            alt={logo.alt} 
            className={`h-12 w-auto object-contain ${logoClassName}`}
            loading="lazy"
          />
        ) : (
          <div key={logo.src} className="flex h-20 w-36 sm:w-40 items-center justify-center rounded-xl bg-white ring-1 ring-[#E3E7FA]">
            <img 
              src={logo.src} 
              alt={logo.alt} 
              className={`${customLogoSize} object-contain`}
              loading="lazy"
            />
          </div>
        )
      })}
    </div>
  )
}

export default CustomerLogos
